import { Component, EventEmitter, Input, OnInit, Output, ViewChildren } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { FilterDefinition, FilterItem, FilterItemType } from '../../filtering/filter-definition.type';
import { FilterSelectionMap, FilteringHelper } from '../../filtering/filtering.helper';
import { FilteringService } from '../../filtering/filtering.service';
import { SelectItem } from '../select-with-search/select-item.type';
import { SelectionHelper } from '../select-with-search/selection';
import { SelectWithSearchComponent } from '../select-with-search/select-with-search.component';

@Component({
  selector: 'shared-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  standalone: false,
})
export class FilterPanelComponent implements OnInit {
  @Input() disabled = false;
  @Input() set items(value: FilterItem[]) {
    if (value == null) {
      return;
    }

    this.filteringService.initializeFilters(value);
  }

  @Output() filterChanged = new EventEmitter<FilterDefinition>();

  @ViewChildren(SelectWithSearchComponent) filterComponents: SelectWithSearchComponent[];

  readonly FilterItemType = FilterItemType;
  filterDefinition: FilterDefinition;
  filterDefinition$: Observable<FilterDefinition>;
  filterSelectionMap: FilterSelectionMap = new Map();

  private isClearingFilters = false;

  constructor(private filteringService: FilteringService) {}

  ngOnInit(): void {
    this.filterDefinition$ = this.filteringService.filterChanged$.pipe(
      tap(args => (this.filterSelectionMap = FilteringHelper.createFilterSelectionMap(args.filter))),
      map(args => args.filter),
      tap(filter => {
        this.filterDefinition = filter;
        if (this.isClearingFilters === false) {
          this.filterChanged.emit(filter);
        }
      }),
    );
  }

  async onFilterValueChanged(selection: SelectItem, filter: FilterItem): Promise<void> {
    if (filter.type !== FilterItemType.Select) {
      return;
    }

    if (selection.id === FilteringHelper.AllItemsId) {
      await this.filteringService.clearFilter(filter.name);
      return;
    }

    await this.filteringService.selectFilterValue(filter.name, selection.id);
  }

  async onFilterClosed(selection: SelectItem[], filter: FilterItem): Promise<void> {
    if (filter.type !== FilterItemType.MultiSelect) {
      return;
    }

    if (filter.clearFilterIfAllSelected && SelectionHelper.allSelected(selection)) {
      await this.filteringService.clearFilter(filter.name);
      return;
    }

    await this.filteringService.selectFilterValue(filter.name, selection.map(i => i.id));
  }

  canClearAllFilters(): boolean {
    return this.filterComponents?.some(c => c.canResetToDefault()) ?? false;
  }

  clearFilters(): void {
    this.isClearingFilters = true;
    try {
      this.filterComponents.filter(c => c.canResetToDefault()).forEach(c => c.resetToDefault());
      this.filterChanged.emit(this.filterDefinition);
    } finally {
      this.isClearingFilters = false;
    }
  }
}
